import { poolFor } from '../engine/facts';
import type { FactId, Tile } from '../engine/facts';
import type { MasteryState } from '../engine/mastery';
import { MASTERY_ORDER } from './mastery-view';

/** Восемнадцать фактов одной таблицы: девять умножений и девять делений
 *  (множитель ходит от 2 до 10). Десять таблиц дают 180 — число, которое
 *  показывает заголовок экрана прогресса. */
export function tileFactIds(tile: Tile): FactId[] {
  return [
    ...poolFor([tile], 'mul').map((f) => f.id),
    ...poolFor([tile], 'div').map((f) => f.id),
  ];
}

export type Counts = Record<MasteryState, number>;

function emptyCounts(): Counts {
  return { mastered: 0, slow: 0, erring: 0, insufficient: 0, never: 0 };
}

/** Сколько фактов таблицы в каждом состоянии.
 *
 *  Факт, которого нет в карте, считается невстреченным, а не пропускается:
 *  иначе сумма долей окажется меньше 18 и кольцо покажет неполную
 *  окружность, молча соврав, что таблица короче. */
export function tileBreakdown(tile: Tile, states: ReadonlyMap<FactId, MasteryState>): Counts {
  const counts = emptyCounts();
  for (const factId of tileFactIds(tile)) {
    counts[states.get(factId) ?? 'never'] += 1;
  }
  return counts;
}

export function sumCounts(all: readonly Counts[]): Counts {
  const total = emptyCounts();
  for (const counts of all) {
    for (const state of MASTERY_ORDER) total[state] += counts[state];
  }
  return total;
}

export interface Arc {
  state: MasteryState;
  /** Длина дуги в единицах длины окружности. */
  length: number;
  /** Смещение начала дуги от нуля, в тех же единицах. */
  offset: number;
}

/** Раскладка кольца: подряд идущие дуги, по одной на непустое состояние.
 *
 *  Пустые состояния выбрасываются, иначе в `<circle>` попадут дуги нулевой
 *  длины: при круглой заглушке линии они рисуются точками и кольцо
 *  покрывается крапинами на границах.
 *
 *  Границы дуг берутся нарастающей суммой долей, поэтому последняя из них
 *  приходится ровно на конец окружности. Практической разницы с умножением
 *  каждой доли по отдельности здесь нет — при пяти дугах расхождение
 *  порядка 1e-14, — но так граница задана одним правилом, а не двумя. */
export function ringArcs(counts: Counts, circumference: number): Arc[] {
  const total = MASTERY_ORDER.reduce((sum, state) => sum + counts[state], 0);
  if (total === 0) return [];

  const arcs: Arc[] = [];
  let done = 0;
  let offset = 0;
  for (const state of MASTERY_ORDER) {
    if (counts[state] === 0) continue;
    const from = (done / total) * circumference;
    done += counts[state];
    const to = (done / total) * circumference;
    arcs.push({ state, length: to - from, offset });
    offset = to;
  }
  return arcs;
}
